import { useEffect, useState } from "react";
import { Appear } from "./fade";
import { loadBureau, type Bureau, type BureauDeal, type BureauLink, type BureauTask } from "./bureau";
import { listCompanyDocuments, type FxOpenDoc } from "./facturx";
import { AccessNote, saveMemberRole, useAccess, type CrmRole } from "./access";
import { BookShareButton } from "./share";
import { currentUserId } from "./auth";
import { Modal } from "./Modal";
import { StageSheet } from "./Suivi";
import { canonStage, stageList, stageOf, type PipelineStage } from "./suivi";

const KINDS: Record<string, string> = { call: "Appeler", quote: "Devis", unpaid: "Impayé", meeting: "RDV" };

function euros(value: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

export function BureauView({ onOpen }: { onOpen: (ficheId: number) => void }) {
  const [board, setBoard] = useState<Bureau | null>(null);
  const [error, setError] = useState("");
  const [view, setView] = useState<"board" | "list">(() => (window.localStorage.getItem("crm_pipeline_view") === "list" ? "list" : "board"));
  const [editing, setEditing] = useState(false);
  const [team, setTeam] = useState(false);
  const [days, setDays] = useState(14);
  const access = useAccess();

  function reload(next = days) {
    void loadBureau(next)
      .then((loaded) => setBoard(loaded))
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Bureau indisponible"));
  }

  useEffect(() => {
    let alive = true;
    void loadBureau(days)
      .then((next) => { if (alive) setBoard(next); })
      .catch((reason: unknown) => { if (alive) setError(reason instanceof Error ? reason.message : "Bureau indisponible"); });
    return () => { alive = false; };
  }, [days]);

  function choose(next: "board" | "list") {
    setView(next);
    window.localStorage.setItem("crm_pipeline_view", next);
  }

  if (!board) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" role="status" aria-label={error || "Ouverture du bureau"}>
        {error ? <p className="text-sm text-rose-700">{error}</p> : <span className="crm-browse-loading-spin" aria-hidden />}
      </div>
    );
  }

  const money = euros(board.pipeline);
  const people = board.people || [];
  const relances = [...board.lanes.late, ...board.lanes.today, ...board.lanes.week];
  const columns = stageList(board.columns);
  const open = columns.filter((stage) => stage.kind === "open");
  const deals = board.deals_list || [];

  return (
    <Appear className="crm-board grid gap-6 px-6 py-6">
      <div className="flex items-center justify-end gap-2">
        <div className="crm-pill flex rounded-full p-1 text-xs font-semibold">
          <button type="button" className={view === "board" ? "crm-fill rounded-full px-3 py-1 text-white" : "rounded-full px-3 py-1"} onClick={() => choose("board")}>Kanban</button>
          <button type="button" className={view === "list" ? "crm-fill rounded-full px-3 py-1 text-white" : "rounded-full px-3 py-1"} onClick={() => choose("list")}>Liste</button>
        </div>
        <BookShareButton />
        {access.canWrite ? <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-xs font-semibold" onClick={() => setEditing(true)}>Étapes</button> : null}
        {access.isAdmin ? <button type="button" className="crm-pill rounded-full px-3 py-1.5 text-xs font-semibold" onClick={() => setTeam(true)}>Équipe</button> : null}
        <AccessNote />
      </div>
      <div className="grid gap-3 lg:grid-cols-4">
        <NameColumn title="Prospects" color="#d97706" rows={people.filter((row) => row.status === "prospect").map((row) => ({ id: row.id, name: row.name, detail: "" }))} onOpen={onOpen} />
        <NameColumn title="Clients" color="#059669" rows={people.filter((row) => row.status === "client").map((row) => ({ id: row.id, name: row.name, detail: "" }))} onOpen={onOpen} />
        <NameColumn title={`En cours · ${money}`} color="#2563eb" rows={(board.deals_list || []).map((row) => ({ id: row.fiche_id, name: row.fiche_name, detail: [row.title.trim(), row.amount > 0 ? euros(row.amount) : ""].filter(Boolean).join(" · ") }))} onOpen={onOpen} />
        <NameColumn title="Relances" color="#e11d48" rows={relances.map((row) => ({ id: row.fiche_id, name: row.fiche_name, detail: [KINDS[row.kind] || row.kind, row.title, showDate(row.due_on)].filter(Boolean).join(" · ") }))} onOpen={onOpen} />
      </div>
      {view === "list" ? (
        <DealTable rows={deals} columns={columns} onOpen={onOpen} />
      ) : (
        <div className="overflow-x-auto">
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(open.length, 1)}, minmax(12rem, 1fr))`, minWidth: `${Math.max(open.length, 1) * 13}rem` }}>
            {open.map((stage) => (
              <StageColumn key={stage.code} title={stage.label} color={stage.color} rows={deals.filter((row) => canonStage(row.status, columns) === stage.code)} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
      {editing ? (
        <Modal onClose={() => { setEditing(false); reload(); }} sheetClass="crm-pop max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl border border-slate-200 p-5">
          {(close) => <StageSheet stages={columns} onClose={() => { close(); reload(); }} />}
        </Modal>
      ) : null}
      {team ? (
        <Modal onClose={() => setTeam(false)} sheetClass="crm-pop max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl border border-slate-200 p-5">
          {(close) => <TeamSheet onClose={close} />}
        </Modal>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sans activité depuis</span>
        {[7, 14, 30].map((span) => (
          <button key={span} type="button" className={`${days === span ? "crm-fill text-white" : "crm-pill"} rounded-full px-3 py-1 text-xs font-semibold`} onClick={() => setDays(span)}>{span} jours</button>
        ))}
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <NameColumn title={`Sans activité · ${board.quiet?.length || 0}`} color="#64748b" rows={(board.quiet || []).map((row) => ({ id: row.id, name: row.name, detail: showDate(row.seen) }))} onOpen={onOpen} />
        <MoneyColumn title="Devis en attente" kind="quote" links={board.links || []} onOpen={onOpen} />
        <MoneyColumn title="Impayés" kind="invoice" links={board.links || []} onOpen={onOpen} />
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <Lane title="En retard" rows={board.lanes.late} late onOpen={onOpen} />
        <Lane title="Aujourd’hui" rows={board.lanes.today} onOpen={onOpen} />
        <Lane title="Cette semaine" rows={board.lanes.week} onOpen={onOpen} />
      </div>
    </Appear>
  );
}

function DealTable({ rows, columns, onOpen }: { rows: BureauDeal[]; columns: PipelineStage[]; onOpen: (ficheId: number) => void }) {
  return (
    <div className="crm-card overflow-x-auto rounded-2xl border border-slate-200">
      <table className="w-full min-w-[40rem] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs text-slate-500">
            <th className="px-3 py-2 font-semibold">Client</th>
            <th className="px-3 py-2 font-semibold">Affaire</th>
            <th className="px-3 py-2 font-semibold">Étape</th>
            <th className="px-3 py-2 font-semibold">Montant</th>
            <th className="px-3 py-2 font-semibold">Probabilité</th>
            <th className="px-3 py-2 font-semibold">Clôture</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr><td className="px-3 py-4 text-slate-400" colSpan={6}>Aucune affaire en cours.</td></tr> : null}
          {rows.map((row) => {
            const stage = stageOf(canonStage(row.status, columns), columns);
            return (
              <tr key={row.fiche_id} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2"><button type="button" className="font-medium hover:underline" onClick={() => onOpen(row.fiche_id)}>{row.fiche_name}</button></td>
                <td className="px-3 py-2">{row.title.trim() || "Sans intitulé"}</td>
                <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: stage?.color || "#94a3b8" }} />{stage?.label || row.status}</span></td>
                <td className="px-3 py-2">{row.amount > 0 ? euros(row.amount) : "—"}</td>
                <td className="px-3 py-2">{row.probability ? `${row.probability} %` : "—"}</td>
                <td className="px-3 py-2">{row.expected_on ? showDate(row.expected_on) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NameColumn({ title, color, rows, onOpen }: { title: string; color: string; rows: { id: number; name: string; detail: string }[]; onOpen: (ficheId: number) => void }) {
  return (
    <section className="crm-card flex flex-col rounded-2xl border border-slate-200 p-4 shadow-sm" style={{ boxShadow: `inset 0 3px 0 ${color}, 0 1px 2px rgb(15 23 42 / 8%)` }}>
      <h2 className="flex shrink-0 items-center gap-2 text-sm font-semibold"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{title}</h2>
      <ul className="crm-scroll mt-3 max-h-36 space-y-2 overflow-auto">
        {rows.length === 0 ? <li className="text-sm text-slate-400">Aucune.</li> : null}
        {rows.map((row) => (
          <li key={`${title}-${row.id}-${row.detail}`} className="rounded-xl border border-slate-200">
            <button type="button" className="w-full px-2 py-1.5 text-left hover:bg-black/5" onClick={() => onOpen(row.id)}>
              <p className="truncate text-sm font-medium">{row.name}</p>
              {row.detail ? <p className="truncate text-xs text-slate-500">{row.detail}</p> : null}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StageColumn({ title, color, rows, onOpen }: { title: string; color: string; rows: BureauDeal[]; onOpen: (ficheId: number) => void }) {
  return (
    <section className="crm-card flex flex-col rounded-2xl border border-slate-200 p-4 shadow-sm" style={{ boxShadow: `inset 0 3px 0 ${color}, 0 1px 2px rgb(15 23 42 / 8%)` }}>
      <h2 className="flex shrink-0 items-center gap-2 text-sm font-semibold"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{title}</h2>
      <ul className="crm-scroll mt-3 max-h-36 space-y-2 overflow-auto">
        {rows.length === 0 ? <li className="text-sm text-slate-400">Aucune.</li> : null}
        {rows.map((row) => (
          <li key={row.fiche_id} className="rounded-xl border border-slate-200">
            <button type="button" className="w-full px-2 py-1.5 text-left hover:bg-black/5" onClick={() => onOpen(row.fiche_id)}>
              <p className="truncate text-sm font-medium">{row.fiche_name}</p>
              <p className="truncate text-xs text-slate-500">{[row.title.trim(), row.amount > 0 ? euros(row.amount) : ""].filter(Boolean).join(" · ") || "Sans intitulé"}</p>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function showDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}

function Lane({ title, rows, late, onOpen }: { title: string; rows: BureauTask[]; late?: boolean; onOpen: (ficheId: number) => void }) {
  return (
    <section className="crm-card rounded-2xl border border-slate-200 p-4 shadow-sm">
      <h2 className="text-sm font-semibold">{title}</h2>
      <ul className="crm-scroll mt-3 max-h-36 space-y-2 overflow-auto">
        {rows.length === 0 ? <li className="text-sm text-slate-400">Rien.</li> : null}
        {rows.map((row) => (
          <li key={row.id} className="rounded-xl border border-slate-200">
            <button type="button" className="w-full px-2 py-1.5 text-left hover:bg-black/5" onClick={() => onOpen(row.fiche_id)}>
              <p className={late ? "text-sm font-medium text-rose-700" : "text-sm font-medium"}>{row.fiche_name}</p>
              <p className="text-xs text-slate-500">{[KINDS[row.kind] || row.kind, row.title, showDate(row.due_on)].filter(Boolean).join(" · ")}</p>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MoneyColumn({ title, kind, links, onOpen }: { title: string; kind: "quote" | "invoice"; links: BureauLink[]; onOpen: (ficheId: number) => void }) {
  const [rows, setRows] = useState<{ id: number; name: string; detail: string }[]>([]);
  const [note, setNote] = useState("");
  const key = links.map((link) => `${link.company_id}:${link.client_id}`).join(",");
  useEffect(() => {
    let alive = true;
    const companies = [...new Set(links.map((link) => link.company_id))];
    const byClient = new Map(links.map((link) => [link.client_id, link]));
    if (companies.length === 0) {
      setRows([]);
      setNote("");
      return () => { alive = false; };
    }
    void Promise.all(companies.map((companyId) => listCompanyDocuments(companyId, kind)))
      .then((packs) => {
        if (!alive) return;
        const next: { id: number; name: string; detail: string }[] = [];
        packs.flat().forEach((doc) => {
          const link = byClient.get(Number(doc.client_id) || 0);
          if (!link || !openPaper(doc, kind)) return;
          const amount = kind === "invoice" ? ((doc.total_ttc_cents || 0) - (doc.paid_cents || 0)) / 100 : (doc.total_ttc_cents || 0) / 100;
          next.push({ id: link.fiche_id, name: link.fiche_name, detail: [doc.number || "", amount > 0 ? euros(amount) : ""].filter(Boolean).join(" · ") });
        });
        setRows(next);
        setNote("");
      })
      .catch(() => { if (alive) setNote("Factures ne répond pas."); });
    return () => { alive = false; };
  }, [kind, key]);
  return (
    <section className="crm-card flex flex-col rounded-2xl border border-slate-200 p-4 shadow-sm">
      <h2 className="text-sm font-semibold">{title} · {rows.length}</h2>
      {note ? <p className="mt-2 text-xs text-amber-700">{note}</p> : null}
      <ul className="crm-scroll mt-3 max-h-36 space-y-2 overflow-auto">
        {rows.length === 0 && !note ? <li className="text-sm text-slate-400">Aucune.</li> : null}
        {rows.map((row, index) => (
          <li key={`${row.id}-${index}`} className="rounded-xl border border-slate-200">
            <button type="button" className="w-full px-2 py-1.5 text-left hover:bg-black/5" onClick={() => onOpen(row.id)}>
              <p className="truncate text-sm font-medium">{row.name}</p>
              {row.detail ? <p className="truncate text-xs text-slate-500">{row.detail}</p> : null}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function openPaper(doc: FxOpenDoc, kind: "quote" | "invoice"): boolean {
  if (doc.status !== "validated") return false;
  if (kind === "quote") return !doc.signed_at && !doc.converted_invoice_id && !doc.refused_at;
  return (doc.total_ttc_cents || 0) - (doc.paid_cents || 0) > 0;
}

function TeamSheet({ onClose }: { onClose: () => void }) {
  const access = useAccess();
  const me = Number(currentUserId() || 0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(0);

  async function choose(userId: number, role: CrmRole) {
    setBusy(userId);
    setError("");
    try {
      await saveMemberRole(userId, role);
      await access.refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Rôle non enregistré");
    } finally {
      setBusy(0);
    }
  }

  return (
    <div className="grid gap-3">
      <h2 className="text-lg font-semibold">Équipe</h2>
      <p className="text-sm text-slate-500">Lecture ne change rien. Commercial tient les fiches. Admin gère aussi les rôles et la suppression définitive.</p>
      {access.members.length === 0 ? <p className="text-sm text-slate-400">Personne dans ce groupe.</p> : null}
      {access.members.map((member) => (
        <label key={member.id} className="flex items-center justify-between gap-3 text-sm">
          <span className="font-medium">{member.name}{member.id === me ? " · toi" : ""}</span>
          <select className="crm-field rounded-xl border border-slate-200 px-3 py-2" value={member.role} disabled={member.id === me || busy === member.id} onChange={(event) => void choose(member.id, event.target.value as CrmRole)}>
            <option value="lecture">Lecture</option>
            <option value="commercial">Commercial</option>
            <option value="admin">Admin</option>
          </select>
        </label>
      ))}
      {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      <button type="button" className="crm-pill justify-self-start rounded-full px-4 py-2 text-sm font-semibold" onClick={onClose}>Fermer</button>
    </div>
  );
}
